/*! Variant picker rewrite for Product -> Style -> Size */
class VariantSelects extends HTMLElement {
  #addToCartText;
  #variantCache;

  constructor() {
    super();
    this.onVariantChange = this.onVariantChange.bind(this);
  }

  connectedCallback() {
    this.uniqueId = this.dataset.card || this.dataset.section;
    this.#addToCartText =
      this.dataset.addToCartText || window.theme?.localize?.("ADD_TO_CART") || "Add to cart";
    this.#variantCache = new Map();

    this.addEventListener("change", this.onVariantChange);
    this.addEventListener("input", this.onVariantChange);
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.onVariantChange);
    this.removeEventListener("input", this.onVariantChange);
    this.#variantCache?.clear();
  }

  get optionInputs() {
    return Array.from(this.querySelectorAll("select, fieldset"));
  }

  get optionValueIds() {
    return this.optionInputs
      .map((el) => {
        if (el.tagName === "SELECT") {
          const selected = el.options[el.selectedIndex];
          return selected?.dataset.optionValueId || "";
        }
        const checked = el.querySelector("input:checked");
        return checked?.dataset.optionValueId || "";
      })
      .filter(Boolean);
  }

  get combinedListingUrls() {
    return Array.from(
      this.querySelectorAll("select option[data-product-url], fieldset input[data-product-url]")
    ).reduce((acc, el) => {
      acc[el.dataset.optionValueId] = el.dataset.productUrl;
      return acc;
    }, {});
  }

  async onVariantChange(event) {
    const target = event.target;
    if (!target) return;

    if (target.tagName === "SELECT") {
      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve))
      );
    }

    this.removeErrorMessage();

    const optionValueIds = this.optionValueIds;
    if (!optionValueIds.length) return;

    const optionValuesString = optionValueIds.join(",");
    const htmlText = await this.fetchVariantSection(optionValuesString);
    if (!htmlText) return;

    const parsed = this.parseHTML(htmlText);
    const newSelectors = parsed.querySelector(`#variant-selectors-${this.id.replace("variant-selectors-", "")}`);
    const newVariantDataElement = parsed.querySelector(`#variant-data-${this.uniqueId}`);
    const productElement = this.closest("product-single, quick-shop");

    if (!newSelectors || !newVariantDataElement || !productElement) return;

    const newVariant = JSON.parse(newVariantDataElement.textContent || "null");

    this.replaceSelectorMarkup(newSelectors);

    if (!newVariant?.id) {
      this.currentVariant = null;
      this.setUnavailable();
      this.updateURL(null);
      return;
    }

    this.currentVariant = newVariant;

    this.swapInnerHTMLById(parsed, `price-${this.uniqueId}`);
    this.swapInnerHTMLById(parsed, `unit-price-${this.uniqueId}`);
    this.swapInnerHTMLById(parsed, `label-${this.uniqueId}`);
    this.swapInnerHTMLById(parsed, `labels-${this.uniqueId}`);
    this.swapInnerHTMLById(parsed, `inventory-notice-${this.uniqueId}`);
    this.swapInnerHTMLById(parsed, `sku-${this.uniqueId}`);
    this.swapInnerHTMLById(parsed, `barcode-${this.uniqueId}`);

    this.replaceProductForm(parsed);
    this.updateVariantInput();
    this.updatePickupAvailability();
    this.updateURL(this.currentVariant);
    this.updateAddButton();

    if (this.hasAttribute("data-update-gallery-on-variant-change")) {
      this.replaceGallery(parsed, productElement);
    } else {
      this.activateFeaturedMedia(productElement, this.currentVariant);
    }

    this.dispatchEvent(
      new CustomEvent("on:variant:change", {
        detail: {
          variant: this.currentVariant,
          mediaGallery: productElement.querySelector("media-gallery"),
        },
      })
    );
  }

  async fetchVariantSection(optionValuesString) {
    if (this.#variantCache.has(optionValuesString)) {
      return this.#variantCache.get(optionValuesString);
    }

    const viewParams = this.dataset.viewId ? `?view=${encodeURIComponent(this.dataset.viewId)}` : "";
    const sectionParams = this.dataset.section
      ? `${viewParams ? "&" : "?"}section_id=${encodeURIComponent(this.dataset.section)}`
      : "";
    const optionParams = optionValuesString
      ? `${viewParams || sectionParams ? "&" : "?"}option_values=${encodeURIComponent(optionValuesString)}`
      : "";

    const [combinedListingOptionId] = Object.keys(this.combinedListingUrls).filter((id) =>
      optionValuesString.split(",").includes(id)
    );

    const baseUrl = combinedListingOptionId
      ? this.combinedListingUrls[combinedListingOptionId]
      : this.dataset.url;

    const cacheBust = `${viewParams || sectionParams || optionParams ? "&" : "?"}_=${Date.now()}`;

    const promise = fetch(`${baseUrl}${viewParams}${sectionParams}${optionParams}${cacheBust}`, {
      cache: "no-store",
      credentials: "same-origin",
    })
      .then((res) => (res.ok ? res.text() : ""))
      .catch(() => "");

    this.#variantCache.set(optionValuesString, promise);
    return promise;
  }

  parseHTML(html) {
    const template = document.createElement("template");
    template.innerHTML = html.trim();
    return template.content;
  }

  replaceSelectorMarkup(newSelectors) {
    this.innerHTML = newSelectors.innerHTML;

    Array.from(this.querySelectorAll("[data-a11y-dialog-show]")).forEach((el) => {
      const popup = document.getElementById(el.dataset.a11yDialogShow);
      if (popup) popup.remove();
    });
  }

  swapInnerHTMLById(parsed, id) {
    const oldEl = document.getElementById(id);
    const newEl = parsed.getElementById(id);
    if (oldEl && newEl) {
      oldEl.innerHTML = newEl.innerHTML;
      oldEl.removeAttribute("hidden");
    }
  }

  replaceProductForm(parsed) {
    const oldFormController = document
      .getElementById(`product-form-${this.uniqueId}`)
      ?.closest("product-form");
    const newFormController = parsed.querySelector("product-form");
    if (oldFormController && newFormController) {
      oldFormController.replaceWith(newFormController);
    }
  }

  replaceGallery(parsed, productElement) {
    const newGallery = parsed.getElementById(`media-gallery-${this.uniqueId}`);
    if (!newGallery || typeof productElement.updateGallery !== "function") return;
    productElement.updateGallery(newGallery);
  }

  activateFeaturedMedia(productElement, variant) {
    const mediaId = variant?.featured_media?.id;
    if (!mediaId) return;

    const gallery = productElement.querySelector("media-gallery");
    if (!gallery) return;

    const targetId = String(mediaId);
    gallery.querySelectorAll("[data-media-id]").forEach((item) => {
      const currentId = String(item.dataset.mediaId || "");
      const isActive =
        currentId === targetId ||
        currentId.endsWith(targetId) ||
        currentId.includes(targetId);

      item.classList.toggle("is-active", isActive);
    });
  }

  updateVariantInput() {
    if (!this.currentVariant?.id) return;

    document
      .querySelectorAll(`#product-form-${this.uniqueId}, #product-form-installment-${this.uniqueId}`)
      .forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });

    const productSingle = this.closest("product-single");
    if (productSingle) {
      productSingle.variant = this.currentVariant;
    }
  }

  updatePickupAvailability() {
    if (!this.currentVariant?.id) return;

    const pickup = document.querySelector(`#products-availability-${this.uniqueId} pickup-availability`);
    if (!pickup) return;

    pickup.removeAttribute("hidden");
    pickup.fetchAvailability(this.currentVariant.id);
  }

  updateURL(variant) {
    if (this.dataset.updateUrl === "false") return;

    const url = new URL(window.location.href);

    if (variant?.id) {
      url.searchParams.set("variant", variant.id);
    } else {
      url.searchParams.delete("variant");
    }

    window.history.replaceState({}, "", url.toString());
  }

  removeErrorMessage() {
    const section = this.closest("section");
    section?.querySelector(".qty-error")?.remove();
  }

  updateAddButton() {
    const form = document.getElementById(`product-form-${this.uniqueId}`);
    if (!form) return;

    const addButton = form.querySelector('[name="add"]');
    const addButtonText = form.querySelector('[name="add"] staged-action-text');
    const buttonsContainer = form.querySelector(".js-product-buttons");

    if (!addButton || !addButtonText || !buttonsContainer) return;

    if (!this.currentVariant) {
      addButton.setAttribute("disabled", "disabled");
      addButtonText.textContent = window.theme?.localize?.("UNAVAILABLE") || "Unavailable";
      buttonsContainer.classList.add("is-disabled");
      return;
    }

    if (this.currentVariant.available) {
      addButton.removeAttribute("disabled");
      addButtonText.textContent = this.#addToCartText;
      buttonsContainer.classList.remove("is-disabled");
    } else {
      addButton.setAttribute("disabled", "disabled");
      addButtonText.textContent = window.theme?.localize?.("QTY_NOTICE_SOLD_OUT") || "Sold out";
      buttonsContainer.classList.add("is-disabled");
    }
  }

  setUnavailable() {
    const form = document.getElementById(`product-form-${this.uniqueId}`);
    if (!form) return;

    const addButton = form.querySelector('[name="add"]');
    const addButtonText = form.querySelector('[name="add"] staged-action-text');
    const buttonsContainer = form.querySelector(".js-product-buttons");
    const price = document.getElementById(`price-${this.uniqueId}`);
    const pickup = document.querySelector(`#products-availability-${this.uniqueId} pickup-availability`);
    const inventoryNotice = document.getElementById(`inventory-notice-${this.uniqueId}`);

    if (inventoryNotice) {
      const wrap = inventoryNotice.querySelector(".stock-note");
      const text = inventoryNotice.querySelector(".stock-note__text");
      wrap?.classList.remove("stock-note--in-stock", "stock-note--low");
      wrap?.classList.add("stock-note--sold");
      if (text) {
        text.innerHTML = window.theme?.localize?.("UNAVAILABLE") || "Unavailable";
      }
    }

    if (pickup) pickup.setAttribute("hidden", "");
    if (price) price.setAttribute("hidden", "");

    if (addButton && addButtonText && buttonsContainer) {
      addButton.setAttribute("disabled", "disabled");
      addButtonText.textContent = window.theme?.localize?.("UNAVAILABLE") || "Unavailable";
      buttonsContainer.classList.add("is-disabled");
    }
  }
}

customElements.define("variant-selects", VariantSelects);

class VariantRadios extends VariantSelects {}
customElements.define("variant-radios", VariantRadios);

class VariantMixedInputs extends VariantSelects {}
customElements.define("variant-mixed-inputs", VariantMixedInputs);

class VariantSwatches extends HTMLElement {
  connectedCallback() {
    const checked = this.querySelector("input:checked");
    this.currentSelection = checked ? checked.value : "";

    const form = document.getElementById(this.dataset.formId);
    const update = () => this.updateLabel();

    if (form) {
      form.addEventListener("change", update);
      form.addEventListener("input", update);
    } else {
      this.addEventListener("change", update);
      this.addEventListener("input", update);
    }

    this.bindHoverEvents();
  }

  updateLabel() {
    const checked = this.querySelector("input:checked");
    if (!checked) return;

    const currentSelection = checked.value;
    if (currentSelection !== this.currentSelection) {
      const labelOptionElement = this.querySelector(".js-option-title");
      if (labelOptionElement) {
        labelOptionElement.textContent = currentSelection;
      }
      this.currentSelection = currentSelection;
    }
  }

  bindHoverEvents() {
    const swatches = this.querySelectorAll(".product-form__swatch__item");
    const labelOptionElement = this.querySelector(".js-option-title");
    if (!labelOptionElement) return;

    let resetTimeout;

    swatches.forEach((swatch) => {
      const label = swatch.querySelector("label");
      const input = swatch.querySelector("input");
      if (!label || !input) return;

      label.addEventListener("mouseenter", () => {
        clearTimeout(resetTimeout);
        labelOptionElement.textContent = input.value;
      });

      label.addEventListener("mouseleave", () => {
        resetTimeout = setTimeout(() => {
          labelOptionElement.textContent = this.currentSelection;
        }, 200);
      });
    });
  }
}

customElements.define("variant-swatches", VariantSwatches);